import { X } from 'lucide-react';
import { usePlaybackStore } from '@/stores';
import type { PracticeHand } from '@/types';

interface PracticeSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Settings panel for practice configuration
 */
export function PracticeSettings({ isOpen, onClose }: PracticeSettingsProps) {
  const config = usePlaybackStore((state) => state.config);
  const { setHand, setWaitMode, toggleMetronome, setMetronomeVolume, setShowNoteNames } =
    usePlaybackStore();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h2 className="text-lg font-semibold">Practice Settings</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Settings content */}
        <div className="p-4 space-y-6">
          {/* Hand selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Practice Hand</label>
            <div className="flex gap-2">
              {(['both', 'right', 'left'] as PracticeHand[]).map((hand) => (
                <button
                  key={hand}
                  onClick={() => setHand(hand)}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    config.hand === hand
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {hand === 'both' ? 'Both' : hand === 'right' ? 'Right' : 'Left'}
                </button>
              ))}
            </div>
          </div>

          {/* Wait mode toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-300">Wait Mode</label>
              <p className="text-xs text-gray-500">Pause until you play the correct notes</p>
            </div>
            <button
              onClick={() => setWaitMode(!config.waitMode)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                config.waitMode ? 'bg-blue-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  config.waitMode ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Metronome toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-300">Metronome</label>
              <p className="text-xs text-gray-500">Play a click on each beat</p>
            </div>
            <button
              onClick={toggleMetronome}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                config.metronome ? 'bg-blue-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  config.metronome ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Metronome volume (shown only if metronome is on) */}
          {config.metronome && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Metronome Volume
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={config.metronomeVolume}
                onChange={(e) => setMetronomeVolume(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          )}

          {/* Show note names */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-300">Show Note Names</label>
              <p className="text-xs text-gray-500">Display note names on the waterfall</p>
            </div>
            <button
              onClick={() => setShowNoteNames(!config.showNoteNames)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                config.showNoteNames ? 'bg-blue-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  config.showNoteNames ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
